<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Public Holidays</h5>
        <p class="page-sub">Define non-working public holidays for attendance and leave calculations</p>
      </div>
      <div class="page-actions">
        <button v-if="permission?.manage_public_holiday" class="btn-primary-action" @click="openCreate">
          <font-awesome-icon icon="plus" /> Add Holiday
        </button>
      </div>
    </div>

    <!-- Year filter -->
    <div class="filter-bar">
      <select class="filter-select" v-model="year" @change="fetchHolidays">
        <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
      </select>
      <select class="filter-select" v-model="countryCode" @change="fetchHolidays">
        <option value="">All Countries</option>
        <option value="NG">Nigeria</option>
        <option value="GB">United Kingdom</option>
        <option value="GH">Ghana</option>
        <option value="ZA">South Africa</option>
        <option value="KE">Kenya</option>
        <option value="US">United States</option>
      </select>
    </div>

    <div v-if="loading" class="sk-table">
      <div class="sk-row-head"></div>
      <div v-for="n in 8" :key="n" class="sk-row"></div>
    </div>

    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Day</th>
            <th>Name</th>
            <th>Country</th>
            <th>Recurring</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="h in holidays" :key="h.id">
            <td class="fw-600">{{ fmtDate(h.date) }}</td>
            <td>{{ dayName(h.date) }}</td>
            <td>{{ h.name }}</td>
            <td>{{ countryName(h.country_code) }}</td>
            <td>
              <span class="chip" :class="h.is_recurring ? 'chip--blue' : 'chip--gray'">
                {{ h.is_recurring ? 'Yearly' : 'One-off' }}
              </span>
            </td>
            <td>
              <button v-if="permission?.manage_public_holiday" class="btn-icon" @click="openEdit(h)" title="Edit"><font-awesome-icon icon="pen" /></button>
              <button v-if="permission?.manage_public_holiday" class="btn-icon btn-icon--red" @click="deleteHoliday(h)" title="Delete"><font-awesome-icon icon="trash" /></button>
            </td>
          </tr>
          <tr v-if="!holidays.length">
            <td colspan="6" class="empty-cell">No holidays for {{ year }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ══ Create / Edit Modal ══ -->
    <div class="modal-overlay" v-if="modal.open" @click.self="modal.open = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">{{ modal.id ? 'Edit Holiday' : 'Add Public Holiday' }}</p>
          <button class="modal-close" @click="modal.open = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label class="form-label">Date <span class="req">*</span></label>
            <input type="date" class="form-input" v-model="modal.date" />
          </div>
          <div class="form-row">
            <label class="form-label">Name <span class="req">*</span></label>
            <input type="text" class="form-input" v-model="modal.name" placeholder="e.g. Christmas Day" />
          </div>
          <div class="form-row">
            <label class="form-label">Country Code</label>
            <select class="form-input" v-model="modal.country_code">
              <option value="">Global</option>
              <option value="NG">Nigeria (NG)</option>
              <option value="GB">United Kingdom (GB)</option>
              <option value="GH">Ghana (GH)</option>
              <option value="ZA">South Africa (ZA)</option>
              <option value="KE">Kenya (KE)</option>
              <option value="US">United States (US)</option>
            </select>
          </div>
          <div class="form-row form-row--check">
            <label class="check-label">
              <input type="checkbox" v-model="modal.is_recurring" />
              <span>Recurring every year (use same month+day)</span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-outline" @click="modal.open = false">Cancel</button>
          <button class="btn-primary-action" :disabled="saving" @click="saveHoliday">
            {{ saving ? 'Saving…' : (modal.id ? 'Update' : 'Add Holiday') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useStore } from 'vuex';

const COUNTRY_NAMES = { NG: 'Nigeria', GB: 'United Kingdom', GH: 'Ghana', ZA: 'South Africa', KE: 'Kenya', US: 'United States' };

export default {
  name: 'PublicHolidaysPage',
  data() {
    const currentYear = new Date().getFullYear();
    return {
      loading: false,
      saving: false,
      year: currentYear,
      years: [currentYear - 1, currentYear, currentYear + 1],
      countryCode: '',
      holidays: [],
      modal: { open: false, id: null, date: '', name: '', country_code: '', is_recurring: true },
      store: useStore(),
    };
  },
  computed: {
    permission() { return this.store.state.getCurrentUserPermission; },
  },
  mounted() { this.fetchHolidays(); },
  methods: {
    async fetchHolidays() {
      this.loading = true;
      try {
        const { data } = await axios.get('/hr/holiday', {
          params: { year: this.year, country_code: this.countryCode || undefined, length: 100 },
        });
        this.holidays = (data.data?.data || []).sort((a, b) => a.date.localeCompare(b.date));
      } catch { this.$toast?.error('Failed to load holidays'); }
      finally { this.loading = false; }
    },
    openCreate() {
      this.modal = { open: true, id: null, date: `${this.year}-01-01`, name: '', country_code: this.countryCode, is_recurring: true };
    },
    openEdit(h) { this.modal = { open: true, ...h }; },
    async saveHoliday() {
      if (!this.modal.date || !this.modal.name) { this.$toast?.error('Date and name are required'); return; }
      this.saving = true;
      try {
        if (this.modal.id) {
          await axios.put(`/hr/holiday/${this.modal.id}`, this.modal);
        } else {
          await axios.post('/hr/holiday', this.modal);
        }
        this.modal.open = false;
        this.$toast?.success(this.modal.id ? 'Holiday updated' : 'Holiday added');
        this.fetchHolidays();
      } catch (e) { this.$toast?.error(e.response?.data?.message || 'Save failed'); }
      finally { this.saving = false; }
    },
    async deleteHoliday(h) {
      if (!confirm(`Delete "${h.name}"?`)) return;
      try { await axios.delete(`/hr/holiday/${h.id}`); this.$toast?.success('Deleted'); this.fetchHolidays(); }
      catch (e) { this.$toast?.error(e.response?.data?.message || 'Delete failed'); }
    },
    fmtDate(d) { return d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
    dayName(d) { return d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long' }) : ''; },
    countryName(code) { return COUNTRY_NAMES[code] || code || 'Global'; },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; }

.filter-bar { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:14px; }
.filter-select { height:38px; padding:0 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:13px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red:hover { color:#dc2626; }

.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:12px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 14px; color:#1a2b45; vertical-align:middle; }
.empty-cell { text-align:center; color:#aab8cc; padding:40px !important; }
.fw-600 { font-weight:600; }

.chip { display:inline-block; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.chip--blue { background:#dbeafe; color:#1d4ed8; }
.chip--gray { background:#f1f5f9; color:#64748b; }

/* Skeleton */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row-head,.sk-row { height:44px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
.sk-row-head { height:40px; background:#f8fafc; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box     { background:#fff; border-radius:14px; width:100%; max-width:460px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title   { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close   { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; }
.modal-body    { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer  { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }
.form-row      { display:flex; flex-direction:column; gap:5px; }
.form-row--check { flex-direction:row; align-items:center; }
.form-label    { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input    { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; }
.form-input:focus { border-color:#004AAD; }
.check-label   { display:flex; align-items:center; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; }
.req { color:#dc2626; }
</style>
